import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import useLanguage from '../../hooks/useLanguage';
import './AccountWorkspaceDialog.css';

function nestedDialog(container) {
  return Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]'))
    .filter(element => element !== container && !element.closest('[inert], [hidden], [aria-hidden="true"]')).pop();
}

export default function AccountWorkspaceDialog({ children, onClose, onNavigate }) {
  const { language } = useLanguage();
  const english = language === 'en-US';
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocusRef = useRef(document.activeElement);
  const [pendingAction, setPendingAction] = useState(null);
  const [busyNotice, setBusyNotice] = useState(false);
  const requestAction = useCallback(action => {
    if (dialogRef.current?.querySelector('[data-account-busy="true"]')) {
      setBusyNotice(true);
      return;
    }
    if (dialogRef.current?.querySelector('[data-account-unsaved="true"]')) {
      setPendingAction(() => action);
      return;
    }
    action();
  }, []);

  useEffect(() => {
    if (!busyNotice) return;
    const observer = new MutationObserver(() => {
      if (!dialogRef.current?.querySelector('[data-account-busy="true"]')) setBusyNotice(false);
    });
    observer.observe(dialogRef.current, { attributes: true, subtree: true, attributeFilter: ['data-account-busy'] });
    return () => observer.disconnect();
  }, [busyNotice]);

  useEffect(() => {
    const previousFocus = previousFocusRef.current;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = oldOverflow;
      requestAnimationFrame(() => { if (previousFocus?.isConnected) previousFocus.focus(); });
    };
  }, []);

  useEffect(() => {
    const onKey = event => {
      const nested = nestedDialog(dialogRef.current);
      if (event.key === 'Escape' && !nested) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (pendingAction) setPendingAction(null);
        else requestAction(onClose);
      }
      if (event.key !== 'Tab') return;
      const container = nested || dialogRef.current;
      const controls = Array.from(container.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]'))
        .filter(element => !element.closest('[hidden], [inert]') && element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); container.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, pendingAction, requestAction]);

  return createPortal(
    <div className="account-workspace-backdrop" data-account-center-layer onMouseDown={event => {
      if (event.target === event.currentTarget && !nestedDialog(dialogRef.current)) requestAction(onClose);
    }}>
      <section className="account-workspace-dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="account-workspace-title" tabIndex={-1}
        onKeyDown={event => { if (event.key !== 'Escape') event.stopPropagation(); }}
        onPaste={event => event.stopPropagation()}
        onClickCapture={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const link = event.target.closest('a[href]');
          if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
          const url = new URL(link.href, window.location.href);
          if (url.origin !== window.location.origin || !dialogRef.current.querySelector('[data-account-unsaved="true"], [data-account-busy="true"]')) return;
          event.preventDefault();
          event.stopPropagation();
          requestAction(() => onNavigate(`${url.pathname}${url.search}${url.hash}`));
        }}>
        <header className="account-workspace-dialog__header">
          <div><strong id="account-workspace-title">{english ? 'Account & Settings' : '账户与设置'}</strong><span>{english ? 'Close to return to your workspace' : '关闭后继续当前创作'}</span></div>
          <button ref={closeRef} type="button" aria-label={english ? 'Close account and settings' : '关闭账户与设置'} onClick={() => requestAction(onClose)}><X size={18} /></button>
        </header>
        {busyNotice && <div className="account-workspace-notice" role="status">{english ? 'Please wait for the current save or upload to finish.' : '正在保存或上传，请完成后再关闭或切换。'}</div>}
        {pendingAction && <div className="account-workspace-notice" role="alert">
          <span>{english ? 'Your profile has unsaved changes.' : '个人资料有未保存的更改。'}</span>
          <button type="button" onClick={() => setPendingAction(null)}>{english ? 'Keep editing' : '继续编辑'}</button>
          <button type="button" onClick={() => { const action = pendingAction; setPendingAction(null); action(); }}>{english ? 'Discard and continue' : '放弃更改并继续'}</button>
        </div>}
        <div className="account-workspace-dialog__body">{children}</div>
      </section>
    </div>, document.body,
  );
}
